import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch } from 'react-redux';

import { useBanner } from '@xingternal/banner';
import { trackEvent } from '@xingternal/hops-tracking';
import { useViewerData } from '@xingternal/hub';

import { AppDispatch } from '@/ducks';
import { createLoggedOutSearchAlert } from '@/ducks/alerts';
import {
  CreateSearchAlertMutation,
  DeleteSearchAlertMutation,
  useCreateSearchAlertMutation,
  useDeleteSearchAlertMutation,
} from '@/graphql/generated';
import { useLoginStates } from '@/hooks';
import { SearchAlertQuery } from '@/types';

import { addAlertToCachedList, deleteAlertFromCache } from '../helpers';

type PropTrackActionData = {
  PropTrackAction?: string;
  PropActionOrigin?: string;
};

type DeleteMutationParams = {
  alertId: string;
  onCompleted?: (response: DeleteSearchAlertMutation) => void;
  propTrackActionData?: PropTrackActionData;
  showError?: boolean;
};

type CreateMutationParams = {
  alertQuery?: SearchAlertQuery;
  email?: string;
  queryId?: string;
  onCompleted?: (response: CreateSearchAlertMutation) => void;
  propTrackActionData?: PropTrackActionData;
};

type UseSearchAlertsParams = {
  disableOptimisticUpdate?: boolean;
};

type UseSearchAlertsValue = {
  createSearchAlert: (params: CreateMutationParams) => void;
  deleteSearchAlert: (params: DeleteMutationParams) => void;
};

const deleteOptimisticUpdate: DeleteSearchAlertMutation = {
  __typename: 'Mutation',
  deleteSearchAlert: {
    __typename: 'Empty',
  },
};

const createOptimisticUpdate = (queryId: string): CreateSearchAlertMutation => ({
  __typename: 'Mutation',
  createSearchAlert: {
    __typename: 'SearchAlertCreationSuccess',
    alert: {
      __typename: 'SearchAlert',
      id: 'optimistic-response-id',
      queryId: queryId,
      query: null,
      name: 'optimistic-response-name',
      newJobCount: null,
      createdAt: new Date().toString(),
    },
  },
});

export const useSearchAlerts = (
  { disableOptimisticUpdate }: UseSearchAlertsParams = { disableOptimisticUpdate: false }
): UseSearchAlertsValue => {
  const { showBanner } = useBanner();
  const { isLoggedIn } = useLoginStates();
  const dispatch = useDispatch<AppDispatch>();

  const [createSearchAlertX1] = useCreateSearchAlertMutation();
  const [deleteSearchAlertX1] = useDeleteSearchAlertMutation();
  const { data } = useViewerData();
  const userId = data?.user?.id;

  const onError = (message = 'SE_GENERIC_ERROR_BODY'): void => {
    showBanner({
      children: <FormattedMessage id={message} />,
      variant: 'error',
    });
  };

  const deleteSearchAlert: UseSearchAlertsValue['deleteSearchAlert'] = ({
    alertId,
    propTrackActionData,
    onCompleted,
    showError = true,
  }) => {
    if (propTrackActionData) {
      trackEvent('PropTrackAction', propTrackActionData);
    }

    if (isLoggedIn) {
      deleteSearchAlertX1({
        variables: { id: alertId as string },
        update: deleteAlertFromCache(alertId as string, userId),
        optimisticResponse: !disableOptimisticUpdate ? deleteOptimisticUpdate : undefined,
        onCompleted: (response) => {
          if (showError && response.deleteSearchAlert?.__typename === 'SearchAlertErrorResponse') {
            onError();
          }

          onCompleted?.(response);
        },
        onError: () => {
          if (showError) {
            onError();
          }
        },
      });
    }
  };

  const createSearchAlert: UseSearchAlertsValue['createSearchAlert'] = ({
    alertQuery,
    email,
    queryId,
    propTrackActionData,
    onCompleted,
  }) => {
    if (propTrackActionData) {
      trackEvent('PropTrackAction', propTrackActionData);
    }

    if (isLoggedIn) {
      if (queryId) {
        createSearchAlertX1({
          variables: { queryId },
          update: addAlertToCachedList(userId),
          optimisticResponse: !disableOptimisticUpdate ? createOptimisticUpdate(queryId) : undefined,
          onCompleted: (response) => {
            if (response.createSearchAlert?.__typename === 'SearchAlertErrorResponse') {
              const { code } = response.createSearchAlert;
              onError(
                code === 'ALERT_EXISTS_ALREADY' ? 'SE_AGENTS_JOBS_ALERT_DUPLICATED_TITLE' : 'SE_GENERIC_ERROR_BODY'
              );
            }

            onCompleted?.(response);
          },
          onError: () => {
            onError();
          },
        });
      }
    } else {
      return dispatch(createLoggedOutSearchAlert({ query: alertQuery, email }));
    }
  };

  return { createSearchAlert, deleteSearchAlert };
};
